import React, { useState, useCallback, useMemo, useEffect } from 'react'
import log from 'loglevel'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import Dropdown from 'react-bootstrap/Dropdown'
import { CheckBoxInput } from 'src/components/Reusable/Inputs'
import { StyledDropdownToggle } from 'src/components/Reusable/StandardDropdown/StandardDropdown.styles'
import { useAccountReceptors } from 'src/components/async/model/receptor/useReceptors'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { getReceptorTemplate } from 'src/xgenerated'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { OverflowDropdownToggle } from 'src/components/Reusable/Dropdown'

export const getReceptorIcon = (modelId: string): JSX.Element | null => {
  try {
    const { logo: ReceptorLogo } = getReceptorTemplate(modelId)
    return <ReceptorLogo height={16} width={16} />
  } catch (error) {
    log.warn(`Failed to load receptor icon for modelId: ${modelId}`, error)
    return null
  }
}

export const getReceptorName = (modelId: string): string => {
  try {
    const { name } = getReceptorTemplate(modelId)
    return name
  } catch (error) {
    log.warn(`Failed to load receptor name for modelId: ${modelId}`, error)
    return modelId
  }
}

export interface ReceptorMenuItemProps {
  receptor: ReceptorRecord
  isSelected: boolean
  onToggle: (receptor: ReceptorRecord) => void
  multiple?: boolean
}

export const ReceptorMenuItem = ({
  receptor,
  isSelected,
  onToggle,
  multiple = true,
}: ReceptorMenuItemProps): JSX.Element => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggle(receptor)
  }

  return (
    <Dropdown.Item onClick={handleClick}>
      <FlexRow align={FlexAlign.CENTER} justify={FlexAlign.FLEX_START} gap={8}>
        {multiple && (
          <CheckBoxInput checked={isSelected} onClick={handleClick} />
        )}
        {getReceptorIcon(receptor.getModelid())}
        {`${getReceptorName(
          receptor.getModelid(),
        )} - ${receptor.getTenantid()}`}
      </FlexRow>
    </Dropdown.Item>
  )
}

export interface ReceptorDropdownToggleProps {
  selectedReceptors: ReceptorRecord[]
}

const getReceptorDisplayName = (r: ReceptorRecord): string => {
  const modelId = r.getModelid()
  const receptorName = getReceptorName(modelId)
  const tenantId = r.getTenantid()
  return `${receptorName} - ${tenantId}`
}

export const ReceptorDropdownToggle = ({
  selectedReceptors,
}: ReceptorDropdownToggleProps): JSX.Element => (
  <OverflowDropdownToggle
    items={selectedReceptors}
    getDisplayName={getReceptorDisplayName}
    getKey={(r) => r.getId()}
    placeholder="Select Receptor"
    tooltipId="more-selected-receptors"
    minWidth={200}
    renderItem={(r) => (
      <FlexRow gap={8} key={r.getId()}>
        {getReceptorIcon(r.getModelid())}
        {getReceptorDisplayName(r)}
      </FlexRow>
    )}
  />
)

// Main components
export const ReceptorDropDown = ({
  initialSelectedReceptors,
  onChange,
  multiple = true,
}: {
  initialSelectedReceptors: ReceptorRecord[]
  onChange: (selectedReceptors: ReceptorRecord[]) => void
  multiple?: boolean
}): JSX.Element => {
  const [selectedReceptors, setSelectedReceptors] = useState<ReceptorRecord[]>(
    [],
  )
  const { data } = useAccountReceptors()
  const receptors = useMemo(
    () => data?.getReceptorsList().filter((r) => r.getIsenabled()) ?? [],
    [data],
  )
  const canUpdateSchedules = useCanPerformAction(
    CAPABILITY.CAN_UPDATE_SCHEDULES,
  )
  useEffect(() => {
    setSelectedReceptors(initialSelectedReceptors)
  }, [initialSelectedReceptors])

  const handleCheckboxChange = (receptor: ReceptorRecord) => {
    const isSelected = selectedReceptors.some(
      (r) => r.getId() === receptor.getId(),
    )
    const newSelectedReceptors = isSelected
      ? selectedReceptors.filter((r) => r.getId() !== receptor.getId())
      : [...selectedReceptors, receptor]

    setSelectedReceptors(newSelectedReceptors)
    onChange(newSelectedReceptors)
  }

  const isReceptorSelected = useCallback(
    (receptor: ReceptorRecord): boolean => {
      return selectedReceptors.some((r) => r.getId() === receptor.getId())
    },
    [selectedReceptors],
  )

  return (
    <Dropdown style={{ width: '100%' }}>
      <Dropdown.Toggle
        as={StyledDropdownToggle}
        style={{ width: '100%', height: '40px' }}
        disabled={!canUpdateSchedules}
      >
        <ReceptorDropdownToggle selectedReceptors={selectedReceptors} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {receptors.map((receptor) => (
          <ReceptorMenuItem
            key={receptor.getId()}
            receptor={receptor}
            isSelected={isReceptorSelected(receptor)}
            onToggle={handleCheckboxChange}
            multiple={multiple}
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}
